import { settings } from './storage.js';
import { device } from './device.js';

/* Capa de red: fetch con timeout y, cuando el servidor no envía cabeceras CORS
   (habitual en paneles Xtream), reintento transparente a través del proxy configurado.
   En Cordova y Electron no hay CORS, así que el proxy no se usa. */

const hostProxyMemo = new Map();

function proxyUrlFor(url) {
    const proxy = (settings.get('proxyUrl') || '').trim();
    if (proxy) {
        return proxy + (proxy.indexOf('?') === -1 ? '?' : '&') + 'url=' + encodeURIComponent(url);
    }
    // Servido por server/serve.js: proxy en el mismo origen.
    if (location.protocol.indexOf('http') === 0 && !device.isCordova && !device.isElectron) {
        // Con cuentas activas, /proxy exige el enlace de reproducción de quien lo pide (?st=).
        const st = net.streamToken ? net.streamToken() : '';
        return location.origin + '/proxy?url=' + encodeURIComponent(url) + (st ? '&st=' + encodeURIComponent(st) : '');
    }
    return null;
}

function hostOf(url) {
    try {
        return new URL(url).host;
    } catch (e) {
        return url;
    }
}

function withTimeout(promise, ms, controller) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
            if (controller) controller.abort();
            reject(new Error('timeout'));
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export const net = {
    /** (url) => dirección a través del servidor de Filoteca; lo pone main.js cuando hay servidor. */
    credentialProxy: null,

    /** () => enlace de reproducción vigente ('' si no hay cuentas); lo pone main.js. */
    streamToken: null,

    /**
     * fetchText / fetchJson intentan primero la URL directa; si falla por CORS o red,
     * reintentan por el proxy (si existe) y recuerdan la elección por host.
     */
    async fetchRaw(url, { timeout = 20000, forceProxy = false } = {}) {
        const host = hostOf(url);
        const useProxy = forceProxy || hostProxyMemo.get(host) === true;
        const attempt = async (target) => {
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const res = await withTimeout(fetch(target, {
                cache: 'no-store',
                signal: controller ? controller.signal : undefined
            }), timeout, controller);
            if (!res.ok) {
                const err = new Error('HTTP ' + res.status);
                err.status = res.status;
                throw err;
            }
            return res;
        };

        const proxied = proxyUrlFor(url);
        if (useProxy && proxied) {
            return attempt(proxied);
        }
        try {
            const res = await attempt(url);
            hostProxyMemo.set(host, false);
            return res;
        } catch (err) {
            // Errores HTTP reales (401/404/5xx) llegan igual por proxy: no reintentar.
            if (err.status || !proxied) throw err;
            const res = await attempt(proxied);
            hostProxyMemo.set(host, true);
            return res;
        }
    },

    async fetchText(url, opts) {
        const res = await this.fetchRaw(url, opts);
        // Las guías suelen publicarse comprimidas (.xml.gz) sin decirlo en las cabeceras.
        const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
        if (!/\.gz(\?|$)/i.test(url) && !/gzip|octet-stream/i.test(type)) return res.text();
        const bytes = new Uint8Array(await res.arrayBuffer());
        if (bytes[0] !== 0x1f || bytes[1] !== 0x8b) return new TextDecoder('utf-8').decode(bytes);
        if (typeof DecompressionStream === 'undefined') throw Object.assign(new Error('gzip'), { code: 'gzip' });
        const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
        return new Response(stream).text();
    },

    async fetchJson(url, opts) {
        const res = await this.fetchRaw(url, opts);
        const text = await res.text();
        try {
            return JSON.parse(text);
        } catch (e) {
            const err = new Error('invalid-json');
            err.body = text.slice(0, 200);
            throw err;
        }
    },

    /** Devuelve la URL que debe usar el reproductor para un stream, aplicando proxy si el host lo necesita. */
    streamUrl(url) {
        const proxied = proxyUrlFor(url);
        // Con usuario y contraseña en la dirección (archivos propios): ni el navegador ni la app
        // piden vídeos así, pero el proxy sí sabe. En la app, el del servidor de Filoteca.
        if (/^https?:\/\/[^/?#@]+@/i.test(url)) return proxied || (this.credentialProxy ? this.credentialProxy(url) : null) || url;
        if (!proxied) return url;
        const memo = hostProxyMemo.get(hostOf(url));
        // Página HTTPS y stream HTTP: el navegador bloquea el contenido mixto, así que siempre por proxy.
        if (location.protocol === 'https:' && /^http:/i.test(url)) return proxied;
        // Directo solo cuando ya se comprobó que el host permite CORS; si no se sabe, por proxy.
        return memo === false ? url : proxied;
    },

    usesProxy(url) {
        return this.streamUrl(url) !== url;
    },

    resetMemo() {
        hostProxyMemo.clear();
    }
};
